/**
 * PaywallProductDialog Component
 *
 * Dialog for creating and editing paywall products.
 * Allows selecting Stripe products/prices and configuring subscription options.
 * Supports inline creation of new Stripe products and prices.
 *
 * @module paywall-products/components/PaywallProductDialog
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { useStripeConnect } from '@modules/stripe-connect';
import type { PaywallProduct } from '@segmently/schema-models/payments';
import type { StripeProduct, StripePrice, StripeCoupon, CreatePriceData } from '@modules/stripe-connect';

/**
 * Props for PaywallProductDialog
 */
export interface PaywallProductDialogProps {
  /** Whether dialog is open */
  open: boolean;

  /** Product to edit (null for create mode) */
  product: PaywallProduct | null;

  /** Project ID */
  projectId: string;

  /** Default mode for new products (matches active tab) */
  defaultMode?: 'test' | 'live';

  /** Close handler */
  onClose: () => void;

  /** Save handler */
  onSave: (data: Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
}

/**
 * Form state for product dialog
 */
interface ProductFormState {
  name: string;
  description: string;
  type: 'one_time' | 'subscription';
  stripeProductId: string;
  stripePriceId: string;
  /** Optional coupon to auto-apply at checkout */
  stripeCouponId: string;
  interval: 'day' | 'week' | 'month' | 'year';
  intervalCount: number;
  trialDays: number;
  paidTrialEnabled: boolean;
  paidTrialPriceId: string;
  /** Stripe mode: test for development, live for production */
  mode: 'test' | 'live';
  /** Checkout mode: embedded for PaymentElement, redirect for Stripe Checkout */
  checkoutMode: 'embedded' | 'redirect';
}

const DEFAULT_FORM_STATE: ProductFormState = {
  name: '',
  description: '',
  type: 'subscription',
  stripeProductId: '',
  stripePriceId: '',
  stripeCouponId: '', // Empty = no coupon (user can enter promo code manually)
  interval: 'month',
  intervalCount: 1,
  trialDays: 0,
  paidTrialEnabled: false,
  paidTrialPriceId: '',
  mode: 'test', // Default to test for safety
  checkoutMode: 'redirect', // Default to redirect (simpler for users)
};

/**
 * PaywallProductDialog Component
 */
export function PaywallProductDialog({
  open,
  product,
  projectId,
  defaultMode = 'test',
  onClose,
  onSave,
}: PaywallProductDialogProps) {
  const guideService = useMemo(() => new GuideService(), []);
  const {
    products: stripeProducts,
    fetchProducts,
    fetchPrices,
    createProduct,
    createPrice,
    loadingProducts,
    coupons: stripeCoupons,
    fetchCoupons,
    loadingCoupons,
  } = useStripeConnect(projectId);

  const [form, setForm] = useState<ProductFormState>(DEFAULT_FORM_STATE);
  const [stripePrices, setStripePrices] = useState<StripePrice[]>([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track if this is the initial form load to avoid resetting selection on mode changes
  // during edit mode initialization
  const isInitialFormLoadRef = React.useRef(true);

  // State for create product dialog
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductDescription, setNewProductDescription] = useState('');
  const [creatingProduct, setCreatingProduct] = useState(false);

  // State for create price dialog
  const [showCreatePrice, setShowCreatePrice] = useState(false);
  const [newPriceAmount, setNewPriceAmount] = useState('');
  const [newPriceCurrency, setNewPriceCurrency] = useState('usd');
  const [newPriceType, setNewPriceType] = useState<'one_time' | 'recurring'>('recurring');
  const [newPriceInterval, setNewPriceInterval] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [newPriceIntervalCount, setNewPriceIntervalCount] = useState(1);
  const [creatingPrice, setCreatingPrice] = useState(false);

  // State for multi-currency prices (additional currencies)
  const [additionalCurrencies, setAdditionalCurrencies] = useState<
    Array<{ currency: string; amount: string }>
  >([]);

  // Available currencies for multi-currency selection
  const AVAILABLE_CURRENCIES = [
    { code: 'usd', label: 'USD - US Dollar', symbol: '$' },
    { code: 'eur', label: 'EUR - Euro', symbol: '€' },
    { code: 'gbp', label: 'GBP - British Pound', symbol: '£' },
    { code: 'rub', label: 'RUB - Russian Ruble', symbol: '₽' },
    { code: 'jpy', label: 'JPY - Japanese Yen', symbol: '¥' },
    { code: 'cad', label: 'CAD - Canadian Dollar', symbol: 'C$' },
    { code: 'aud', label: 'AUD - Australian Dollar', symbol: 'A$' },
    { code: 'brl', label: 'BRL - Brazilian Real', symbol: 'R$' },
    { code: 'inr', label: 'INR - Indian Rupee', symbol: '₹' },
    { code: 'mxn', label: 'MXN - Mexican Peso', symbol: 'Mex$' },
    { code: 'cny', label: 'CNY - Chinese Yuan', symbol: '¥' },
    { code: 'chf', label: 'CHF - Swiss Franc', symbol: 'Fr' },
    { code: 'pln', label: 'PLN - Polish Zloty', symbol: 'zł' },
  ];

  // State for create trial fee price dialog (one-time price for paid trial)
  const [showCreateTrialFeePrice, setShowCreateTrialFeePrice] = useState(false);
  const [trialFeeAmount, setTrialFeeAmount] = useState('');
  const [trialFeeCurrency, setTrialFeeCurrency] = useState('usd');
  const [creatingTrialFeePrice, setCreatingTrialFeePrice] = useState(false);

  // Load Stripe products and coupons when dialog opens
  useEffect(() => {
    if (open) {
      fetchProducts(form.mode);
      fetchCoupons(form.mode);
      setStripePrices([]); // Reset prices when dialog opens
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchProducts, fetchCoupons]); // Note: form.mode handled separately below

  // Reload products/coupons and reset selection when mode changes (by user action only)
  useEffect(() => {
    if (open) {
      fetchProducts(form.mode);
      fetchCoupons(form.mode);
      // Only reset product/price/coupon selection when mode is changed by USER ACTION,
      // not during initial form load (which sets mode from existing product)
      if (!isInitialFormLoadRef.current) {
        setForm((prev) => ({
          ...prev,
          stripeProductId: '',
          stripePriceId: '',
          stripeCouponId: '',
          paidTrialPriceId: '',
        }));
        setStripePrices([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.mode]); // Only react to mode changes

  // Load prices when product is selected
  useEffect(() => {
    if (form.stripeProductId) {
      setLoadingPrices(true);
      fetchPrices(form.stripeProductId, form.mode)
        .then((prices) => setStripePrices(prices))
        .finally(() => setLoadingPrices(false));
    } else {
      setStripePrices([]);
    }
  }, [form.stripeProductId, form.mode, fetchPrices]);

  // Auto-sync form.interval/intervalCount from the selected Stripe Price's recurring.
  // Stripe bills strictly by price.recurring.interval — our Firestore label must match
  // or downstream code (billing_cycle_anchor, UI display) drifts. Source of truth is
  // the Price the user already selected, so propagating it here is not a silent change.
  useEffect(() => {
    if (form.type !== 'subscription') return;
    if (!form.stripePriceId) return;
    const selected = stripePrices.find((p) => p.id === form.stripePriceId);
    if (!selected?.recurring) return;
    const { interval, intervalCount } = selected.recurring;
    setForm((prev) => {
      if (prev.interval === interval && prev.intervalCount === intervalCount) return prev;
      return { ...prev, interval, intervalCount };
    });
  }, [form.stripePriceId, form.type, stripePrices]);

  const stripeLoading = loadingProducts || loadingPrices;

  // Initialize form when product changes
  useEffect(() => {
    // Mark as initial load to prevent mode change effect from resetting selection
    isInitialFormLoadRef.current = true;

    if (product) {
      // Editing existing product - use product's mode
      setForm({
        name: product.name,
        description: product.description || '',
        type: product.type,
        stripeProductId: product.stripeProductId ?? '',
        stripePriceId: product.stripePriceId ?? '',
        stripeCouponId: product.stripeCouponId ?? '',
        interval: product.subscription?.interval || 'month',
        intervalCount: product.subscription?.intervalCount || 1,
        trialDays: product.subscription?.trialDays || 0,
        paidTrialEnabled: product.subscription?.paidTrial?.enabled || false,
        paidTrialPriceId: product.subscription?.paidTrial?.stripePriceId || '',
        mode: product.mode || 'test',
        checkoutMode: product.checkoutMode || 'redirect',
      });
    } else {
      // Creating new product - use defaultMode from active tab
      setForm({ ...DEFAULT_FORM_STATE, mode: defaultMode });
    }
    setError(null);

    // Allow mode change resets after a short delay (after initial effects run)
    const timer = setTimeout(() => {
      isInitialFormLoadRef.current = false;
    }, 100);

    return () => clearTimeout(timer);
  }, [product, open, defaultMode]);

  const handleChange = useCallback(
    (field: keyof ProductFormState) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }) => {
        const value = event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
      },
    []
  );

  const handleSwitchChange = useCallback(
    (field: keyof ProductFormState) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [field]: event.target.checked }));
      },
    []
  );

  const handleProductSelect = useCallback(
    (event: { target: { value: unknown } }) => {
      const productId = event.target.value as string;
      const selectedProduct = stripeProducts.find((p) => p.id === productId);
      setForm((prev) => ({
        ...prev,
        stripeProductId: productId,
        stripePriceId: '', // Reset price when product changes
        name: prev.name || selectedProduct?.name || '',
        description: prev.description || selectedProduct?.description || '',
      }));
    },
    [stripeProducts]
  );

  const handleSubmit = useCallback(async () => {
    // Validation
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!form.stripeProductId) {
      setError('Please select a Stripe product');
      return;
    }
    if (!form.stripePriceId) {
      setError('Please select a price');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const data: Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'> = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        type: form.type,
        stripeProductId: form.stripeProductId,
        stripePriceId: form.stripePriceId,
        stripeCouponId: form.stripeCouponId || undefined,
        mode: form.mode,
        checkoutMode: form.checkoutMode,
      };

      // Add subscription config if subscription type
      if (form.type === 'subscription') {
        data.subscription = {
          interval: form.interval,
          intervalCount: form.intervalCount,
        };

        if (form.trialDays > 0) {
          data.subscription.trialDays = form.trialDays;
        }

        if (form.paidTrialEnabled && form.paidTrialPriceId) {
          data.subscription.paidTrial = {
            enabled: true,
            stripePriceId: form.paidTrialPriceId,
          };
        }
      }

      await onSave(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setSaving(false);
    }
  }, [form, onSave]);

  /**
   * Handle creating a new Stripe product
   */
  const handleCreateProduct = useCallback(async () => {
    if (!newProductName.trim()) {
      setError('Product name is required');
      return;
    }

    setCreatingProduct(true);
    setError(null);

    try {
      // Pass `options` only for a live-mode call (P7) — the request body must
      // stay byte-identical to the pre-P7 shape for test-mode calls.
      const product = form.mode === 'live'
        ? await createProduct(newProductName.trim(), newProductDescription.trim() || undefined, form.mode, { confirmLive: true })
        : await createProduct(newProductName.trim(), newProductDescription.trim() || undefined, form.mode);
      // Select the newly created product
      setForm((prev) => ({
        ...prev,
        stripeProductId: product.id,
        stripePriceId: '',
        name: prev.name || product.name,
        description: prev.description || product.description || '',
      }));
      // Reset and close the dialog
      setNewProductName('');
      setNewProductDescription('');
      setShowCreateProduct(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create product');
    } finally {
      setCreatingProduct(false);
    }
  }, [newProductName, newProductDescription, createProduct]);

  /**
   * Handle creating a new Stripe price
   * Supports multi-currency via additionalCurrencies state
   */
  const handleCreatePrice = useCallback(async () => {
    const amount = parseFloat(newPriceAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Valid price amount is required');
      return;
    }

    if (!form.stripeProductId) {
      setError('Please select a product first');
      return;
    }

    // Validate additional currencies
    for (const curr of additionalCurrencies) {
      const currAmount = parseFloat(curr.amount);
      if (isNaN(currAmount) || currAmount <= 0) {
        setError(`Invalid amount for ${curr.currency.toUpperCase()}`);
        return;
      }
    }

    setCreatingPrice(true);
    setError(null);

    try {
      const priceData: CreatePriceData = {
        productId: form.stripeProductId,
        unitAmount: Math.round(amount * 100), // Convert to cents
        currency: newPriceCurrency,
        type: newPriceType,
      };

      if (newPriceType === 'recurring') {
        priceData.recurring = {
          interval: newPriceInterval,
          intervalCount: newPriceIntervalCount,
        };
      }

      // Add multi-currency options if any additional currencies defined
      if (additionalCurrencies.length > 0) {
        priceData.currencyOptions = {};
        for (const curr of additionalCurrencies) {
          const currAmount = parseFloat(curr.amount);
          priceData.currencyOptions[curr.currency.toLowerCase()] = {
            unitAmount: Math.round(currAmount * 100), // Convert to cents
          };
        }
      }

      // Pass `options` only for a live-mode call (P7) — the request body must
      // stay byte-identical to the pre-P7 shape for test-mode calls.
      const price = form.mode === 'live'
        ? await createPrice(priceData, form.mode, { confirmLive: true })
        : await createPrice(priceData, form.mode);

      // Refresh prices list and select the new price
      const updatedPrices = await fetchPrices(form.stripeProductId, form.mode);
      setStripePrices(updatedPrices);
      setForm((prev) => ({ ...prev, stripePriceId: price.id }));

      // Reset and close the dialog
      setNewPriceAmount('');
      setNewPriceCurrency('usd');
      setNewPriceType('recurring');
      setNewPriceInterval('month');
      setNewPriceIntervalCount(1);
      setAdditionalCurrencies([]);
      setShowCreatePrice(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create price');
    } finally {
      setCreatingPrice(false);
    }
  }, [
    newPriceAmount,
    newPriceCurrency,
    newPriceType,
    newPriceInterval,
    newPriceIntervalCount,
    additionalCurrencies,
    form.stripeProductId,
    form.mode,
    createPrice,
    fetchPrices,
  ]);

  /**
   * Handle creating a new trial fee price (one-time price for paid trial)
   */
  const handleCreateTrialFeePrice = useCallback(async () => {
    const amount = parseFloat(trialFeeAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Valid trial fee amount is required');
      return;
    }

    if (!form.stripeProductId) {
      setError('Please select a product first');
      return;
    }

    setCreatingTrialFeePrice(true);
    setError(null);

    try {
      const priceData: CreatePriceData = {
        productId: form.stripeProductId,
        unitAmount: Math.round(amount * 100), // Convert to cents
        currency: trialFeeCurrency,
        type: 'one_time', // Trial fee is always one-time
        nickname: 'Trial Fee', // Add a descriptive nickname
      };

      // Pass `options` only for a live-mode call (P7) — the request body must
      // stay byte-identical to the pre-P7 shape for test-mode calls.
      const price = form.mode === 'live'
        ? await createPrice(priceData, form.mode, { confirmLive: true })
        : await createPrice(priceData, form.mode);

      // Refresh prices list and select the new price as trial fee
      const updatedPrices = await fetchPrices(form.stripeProductId, form.mode);
      setStripePrices(updatedPrices);
      setForm((prev) => ({ ...prev, paidTrialPriceId: price.id }));

      // Reset and close the dialog
      setTrialFeeAmount('');
      setTrialFeeCurrency('usd');
      setShowCreateTrialFeePrice(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create trial fee price');
    } finally {
      setCreatingTrialFeePrice(false);
    }
  }, [trialFeeAmount, trialFeeCurrency, form.stripeProductId, form.mode, createPrice, fetchPrices]);

  const isEditing = !!product;

  // Get one-time prices for paid trial selection (filter recurring prices out)
  const oneTimePrices = stripePrices.filter((p) => p.type === 'one_time');
  const recurringPrices = stripePrices.filter((p) => p.type === 'recurring');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="pp-dialog">
      <DialogTitle
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr 40px',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <IconButton
            onClick={onClose}
            disabled={saving}
            size="small"
            aria-label="close dialog"
            data-testid="pp-close"
            sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          component="span"
          sx={{
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {isEditing ? 'Edit Paywall Product' : 'Create Paywall Product'}
        </Box>
        <Box />
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Stripe Mode Indicator */}
          <WithGuideHelp
            guideKey={GUIDE_ANCHORS.paywallProductBasicFields.guideKey}
            guideService={guideService}
            data-testid={GUIDE_ANCHORS.paywallProductBasicFields.testId}
            position="top-right"
            sx={{ display: 'block' }}
          >
            <Alert
              severity={form.mode === 'test' ? 'warning' : 'success'}
              sx={{ mb: 0 }}
            >
              <Typography variant="body2">
                {form.mode === 'test'
                  ? '🧪 Test Mode — Using Stripe test products/prices for development'
                  : '🚀 Live Mode — Using Stripe live products/prices for production'}
              </Typography>
            </Alert>
          </WithGuideHelp>

          {/* Mode Selection */}
          <FormControl fullWidth>
            <InputLabel>Stripe Mode</InputLabel>
            <Select
              value={form.mode}
              label="Stripe Mode"
              onChange={handleChange('mode') as any}
              data-testid="pp-stripe-mode"
            >
              <MenuItem value="test">🧪 Test Mode</MenuItem>
              <MenuItem value="live">🚀 Live Mode</MenuItem>
            </Select>
          </FormControl>

          {/* Checkout Mode Selection */}
          <FormControl fullWidth>
            <InputLabel>Checkout Mode</InputLabel>
            <Select
              value={form.checkoutMode}
              label="Checkout Mode"
              onChange={handleChange('checkoutMode') as any}
              data-testid="pp-checkout-mode"
            >
              <MenuItem value="redirect">Redirect to Stripe Checkout</MenuItem>
              <MenuItem value="embedded">Embedded Payment Form (Bottom Sheet)</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }} />

          {/* Basic Info */}
          <TextField
            label="Product Name"
            value={form.name}
            onChange={handleChange('name')}
            fullWidth
            required
            helperText="Display name in your app"
            data-testid="pp-name"
          />

          <TextField
            label="Description"
            value={form.description}
            onChange={handleChange('description')}
            fullWidth
            multiline
            rows={2}
            data-testid="pp-description"
          />

          {/* Product Type */}
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={form.type}
              label="Type"
              onChange={handleChange('type') as any}
              data-testid="pp-type"
            >
              <MenuItem value="subscription">Subscription</MenuItem>
              <MenuItem value="one_time">One-time Payment</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }} />

          {/* Stripe Product Selection */}
          <Typography variant="subtitle2" color="text.secondary">
            <WithGuideHelp
              guideKey={GUIDE_ANCHORS.paywallProductStripeMapping.guideKey}
              guideService={guideService}
              data-testid={GUIDE_ANCHORS.paywallProductStripeMapping.testId}
              position="inline-end"
            >
              Stripe Product & Price
            </WithGuideHelp>
            <WithGuideHelp
              guideKey={GUIDE_ANCHORS.paywallProductPriceSetup.guideKey}
              guideService={guideService}
              data-testid={GUIDE_ANCHORS.paywallProductPriceSetup.testId}
              position="inline-end"
            >
              <Box component="span" sx={{ width: 0, height: 0, overflow: 'hidden' }} />
            </WithGuideHelp>
          </Typography>

          {/* Product Selector with Create Button */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Stripe Product</InputLabel>
              <Select
                value={form.stripeProductId}
                label="Stripe Product"
                onChange={handleProductSelect as any}
                disabled={stripeLoading || showCreateProduct}
                data-testid="pp-stripe-product"
              >
                {stripeProducts.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    <Box>
                      <Typography variant="body1">{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.id}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Create new product">
              <IconButton
                onClick={() => setShowCreateProduct(!showCreateProduct)}
                color={showCreateProduct ? 'primary' : 'default'}
                sx={{ mt: 1 }}
                data-testid="pp-create-product-btn"
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Create Product Form */}
          {showCreateProduct && (
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Create New Stripe Product
              </Typography>
              <TextField
                label="Product Name"
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 1.5 }}
                disabled={creatingProduct}
                data-testid="pp-new-product-name"
              />
              <TextField
                label="Description (optional)"
                value={newProductDescription}
                onChange={(e) => setNewProductDescription(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 1.5 }}
                disabled={creatingProduct}
                data-testid="pp-new-product-desc"
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={() => {
                    setShowCreateProduct(false);
                    setNewProductName('');
                    setNewProductDescription('');
                  }}
                  disabled={creatingProduct}
                  data-testid="pp-new-product-cancel"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCreateProduct}
                  disabled={creatingProduct || !newProductName.trim()}
                  startIcon={creatingProduct && <CircularProgress size={14} color="inherit" />}
                  data-testid="pp-new-product-submit"
                >
                  Create
                </Button>
              </Box>
            </Box>
          )}

          {/* Price Selector with Create Button */}
          {form.stripeProductId && (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Price</InputLabel>
                  <Select
                    value={form.stripePriceId}
                    label="Price"
                    onChange={handleChange('stripePriceId') as any}
                    disabled={stripeLoading || stripePrices.length === 0 || showCreatePrice}
                    data-testid="pp-stripe-price"
                  >
                    {/* Filter prices based on product type: subscription shows recurring, one_time shows one_time */}
                    {(form.type === 'subscription' ? recurringPrices : oneTimePrices).map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {formatPriceLabel(p)}
                      </MenuItem>
                    ))}
                  </Select>
                  {/* Show helper if no matching prices */}
                  {form.stripeProductId && !stripeLoading &&
                   (form.type === 'subscription' ? recurringPrices : oneTimePrices).length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      No {form.type === 'subscription' ? 'recurring' : 'one-time'} prices found for this product
                    </Typography>
                  )}
                </FormControl>
                <Tooltip title="Create new price">
                  <IconButton
                    onClick={() => setShowCreatePrice(!showCreatePrice)}
                    color={showCreatePrice ? 'primary' : 'default'}
                    sx={{ mt: 1 }}
                    data-testid="pp-create-price-btn"
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Create Price Form */}
              {showCreatePrice && (
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Create New Price
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                    <TextField
                      label="Amount"
                      type="number"
                      value={newPriceAmount}
                      onChange={(e) => setNewPriceAmount(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      inputProps={{ min: 0.01, step: 0.01 }}
                      disabled={creatingPrice}
                      data-testid="pp-new-price-amount"
                    />
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={newPriceCurrency}
                        label="Currency"
                        onChange={(e) => setNewPriceCurrency(e.target.value as string)}
                        disabled={creatingPrice}
                        data-testid="pp-new-price-currency"
                      >
                        <MenuItem value="usd">USD</MenuItem>
                        <MenuItem value="eur">EUR</MenuItem>
                        <MenuItem value="gbp">GBP</MenuItem>
                        <MenuItem value="rub">RUB</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Price Type</InputLabel>
                      <Select
                        value={newPriceType}
                        label="Price Type"
                        onChange={(e) => setNewPriceType(e.target.value as 'one_time' | 'recurring')}
                        disabled={creatingPrice}
                        data-testid="pp-new-price-type"
                      >
                        <MenuItem value="recurring">Recurring</MenuItem>
                        <MenuItem value="one_time">One-time</MenuItem>
                      </Select>
                    </FormControl>
                    {newPriceType === 'recurring' && (
                      <>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel>Interval</InputLabel>
                          <Select
                            value={newPriceInterval}
                            label="Interval"
                            onChange={(e) => setNewPriceInterval(e.target.value as 'day' | 'week' | 'month' | 'year')}
                            disabled={creatingPrice}
                            data-testid="pp-new-price-interval"
                          >
                            <MenuItem value="day">Day</MenuItem>
                            <MenuItem value="week">Week</MenuItem>
                            <MenuItem value="month">Month</MenuItem>
                            <MenuItem value="year">Year</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          label="Count"
                          type="number"
                          value={newPriceIntervalCount}
                          onChange={(e) => setNewPriceIntervalCount(parseInt(e.target.value) || 1)}
                          size="small"
                          sx={{ width: 70 }}
                          inputProps={{ min: 1, max: 12 }}
                          disabled={creatingPrice}
                          data-testid="pp-new-price-interval-count"
                        />
                      </>
                    )}
                  </Box>

                  {/* Multi-Currency Section */}
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Additional Currencies (optional)
                  </Typography>

                  {/* List of additional currencies */}
                  {additionalCurrencies.map((curr, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Currency</InputLabel>
                        <Select
                          value={curr.currency}
                          label="Currency"
                          data-testid={`pp-new-price-additional-currency-${index}`}
                          onChange={(e) => {
                            const updated = [...additionalCurrencies];
                            updated[index].currency = e.target.value as string;
                            setAdditionalCurrencies(updated);
                          }}
                          disabled={creatingPrice}
                        >
                          {AVAILABLE_CURRENCIES
                            .filter((c) => c.code !== newPriceCurrency && !additionalCurrencies.some((ac, i) => i !== index && ac.currency === c.code))
                            .map((c) => (
                              <MenuItem key={c.code} value={c.code}>
                                {c.code.toUpperCase()}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Amount"
                        type="number"
                        value={curr.amount}
                        onChange={(e) => {
                          const updated = [...additionalCurrencies];
                          updated[index].amount = e.target.value;
                          setAdditionalCurrencies(updated);
                        }}
                        size="small"
                        sx={{ flex: 1 }}
                        inputProps={{ min: 0.01, step: 0.01 }}
                        disabled={creatingPrice}
                        placeholder={`Amount in ${curr.currency.toUpperCase()}`}
                        data-testid={`pp-new-price-additional-amount-${index}`}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          setAdditionalCurrencies(additionalCurrencies.filter((_, i) => i !== index));
                        }}
                        disabled={creatingPrice}
                        color="error"
                        data-testid={`pp-new-price-remove-additional-currency-${index}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}

                  {/* Add Currency Button */}
                  {additionalCurrencies.length < 5 && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        // Find first available currency not already used
                        const usedCurrencies = [newPriceCurrency, ...additionalCurrencies.map((c) => c.currency)];
                        const availableCurrency = AVAILABLE_CURRENCIES.find((c) => !usedCurrencies.includes(c.code));
                        if (availableCurrency) {
                          setAdditionalCurrencies([...additionalCurrencies, { currency: availableCurrency.code, amount: '' }]);
                        }
                      }}
                      disabled={creatingPrice}
                      sx={{ mb: 1.5 }}
                      data-testid="pp-new-price-add-currency"
                    >
                      Add Currency
                    </Button>
                  )}

                  {additionalCurrencies.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Checkout will show the price in customer's local currency if available.
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setShowCreatePrice(false);
                        setNewPriceAmount('');
                        setAdditionalCurrencies([]);
                      }}
                      disabled={creatingPrice}
                      data-testid="pp-new-price-cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleCreatePrice}
                      disabled={creatingPrice || !newPriceAmount}
                      startIcon={creatingPrice && <CircularProgress size={14} color="inherit" />}
                      data-testid="pp-new-price-submit"
                    >
                      Create
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Coupon Selection (optional - auto-apply at checkout) */}
          {form.stripePriceId && (
            <>
              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                Automatic Discount (Optional)
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Stripe Coupon</InputLabel>
                <Select
                  value={form.stripeCouponId}
                  label="Stripe Coupon"
                  onChange={handleChange('stripeCouponId') as any}
                  disabled={loadingCoupons}
                  data-testid="pp-coupon"
                >
                  <MenuItem value="">
                    <em>None (user can enter promo code manually)</em>
                  </MenuItem>
                  {stripeCoupons.map((coupon) => (
                    <MenuItem key={coupon.id} value={coupon.id}>
                      <Box>
                        <Typography variant="body2">
                          {coupon.name || coupon.id}
                          {' — '}
                          {formatCouponDiscount(coupon)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCouponDuration(coupon)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {form.stripeCouponId && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    This coupon will be applied automatically at checkout. Manual promo codes will be disabled.
                  </Typography>
                </Alert>
              )}

              {!loadingCoupons && stripeCoupons.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No active coupons found. Create coupons in your{' '}
                  <a href="https://dashboard.stripe.com/coupons" target="_blank" rel="noopener noreferrer">
                    Stripe Dashboard
                  </a>
                  .
                </Typography>
              )}
            </>
          )}

          {/* Subscription Options */}
          {form.type === 'subscription' && (
            <>
              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                <WithGuideHelp
                  guideKey={GUIDE_ANCHORS.paywallProductSubscriptionOptions.guideKey}
                  guideService={guideService}
                  data-testid={GUIDE_ANCHORS.paywallProductSubscriptionOptions.testId}
                  position="inline-end"
                >
                  Subscription Options
                </WithGuideHelp>
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Billing Interval</InputLabel>
                  <Select
                    value={form.interval}
                    label="Billing Interval"
                    onChange={handleChange('interval') as any}
                    data-testid="pp-interval"
                  >
                    <MenuItem value="day">Daily</MenuItem>
                    <MenuItem value="week">Weekly</MenuItem>
                    <MenuItem value="month">Monthly</MenuItem>
                    <MenuItem value="year">Yearly</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Every X"
                  type="number"
                  value={form.intervalCount}
                  onChange={handleChange('intervalCount')}
                  sx={{ width: 100 }}
                  inputProps={{ min: 1, max: 12 }}
                  data-testid="pp-interval-count"
                />
              </Box>

              <TextField
                label={form.paidTrialEnabled ? "Trial Period (days)" : "Free Trial (days)"}
                type="number"
                value={form.trialDays}
                onChange={handleChange('trialDays')}
                fullWidth
                inputProps={{ min: 0, max: 365 }}
                helperText={form.paidTrialEnabled
                  ? "Duration of the paid trial period"
                  : "Leave 0 for no free trial"}
                data-testid="pp-trial-days"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.paidTrialEnabled}
                    onChange={handleSwitchChange('paidTrialEnabled')}
                    data-testid="pp-paid-trial-switch"
                  />
                }
                label="Paid Trial (one-time setup fee)"
              />

              {form.paidTrialEnabled && (
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <FormControl sx={{ flex: 1 }}>
                      <InputLabel>Trial Fee Price</InputLabel>
                      <Select
                        value={form.paidTrialPriceId}
                        label="Trial Fee Price"
                        onChange={handleChange('paidTrialPriceId') as any}
                        disabled={oneTimePrices.length === 0 || showCreateTrialFeePrice}
                        data-testid="pp-trial-fee-price"
                      >
                        {oneTimePrices.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {formatPriceLabel(p)}
                          </MenuItem>
                        ))}
                      </Select>
                      {oneTimePrices.length === 0 && !showCreateTrialFeePrice && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          No one-time prices found. Click + to create one.
                        </Typography>
                      )}
                    </FormControl>
                    <Tooltip title="Create trial fee price">
                      <IconButton
                        onClick={() => setShowCreateTrialFeePrice(!showCreateTrialFeePrice)}
                        color={showCreateTrialFeePrice ? 'primary' : 'default'}
                        sx={{ mt: 1 }}
                        data-testid="pp-create-trial-price-btn"
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Create Trial Fee Price Form */}
                  {showCreateTrialFeePrice && (
                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Create One-Time Trial Fee Price
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                        <TextField
                          label="Amount"
                          type="number"
                          value={trialFeeAmount}
                          onChange={(e) => setTrialFeeAmount(e.target.value)}
                          size="small"
                          sx={{ flex: 1 }}
                          inputProps={{ min: 0.01, step: 0.01 }}
                          disabled={creatingTrialFeePrice}
                          placeholder="e.g. 0.99"
                          data-testid="pp-trial-fee-amount"
                        />
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <InputLabel>Currency</InputLabel>
                          <Select
                            value={trialFeeCurrency}
                            label="Currency"
                            onChange={(e) => setTrialFeeCurrency(e.target.value as string)}
                            disabled={creatingTrialFeePrice}
                            data-testid="pp-trial-fee-currency"
                          >
                            <MenuItem value="usd">USD</MenuItem>
                            <MenuItem value="eur">EUR</MenuItem>
                            <MenuItem value="gbp">GBP</MenuItem>
                            <MenuItem value="rub">RUB</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        This one-time fee will be charged immediately when customer starts the trial.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setShowCreateTrialFeePrice(false);
                            setTrialFeeAmount('');
                          }}
                          disabled={creatingTrialFeePrice}
                          data-testid="pp-trial-fee-cancel"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleCreateTrialFeePrice}
                          disabled={creatingTrialFeePrice || !trialFeeAmount}
                          startIcon={creatingTrialFeePrice && <CircularProgress size={14} color="inherit" />}
                          data-testid="pp-trial-fee-submit"
                        >
                          Create
                        </Button>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || stripeLoading}
          startIcon={saving && <CircularProgress size={16} color="inherit" />}
          data-testid="pp-submit"
        >
          {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Product'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Format price for display in dropdown
 * Shows concrete regional amounts if price has additional currencies
 */
function formatPriceLabel(price: StripePrice): string {
  const amount = formatCurrencyAmount(price.currency, price.unitAmount);

  // Stripe can include the base currency in currency_options, but that is not
  // a regional price. Show the actual regional prices instead of a count/icon.
  const regionalPrices = price.currencyOptions
    ? Object.entries(price.currencyOptions)
      .filter(([currency]) => currency.toLowerCase() !== price.currency.toLowerCase())
      .map(([currency, option]) => `${currency.toUpperCase()} ${formatCurrencyAmount(currency, option.unitAmount)}`)
    : [];
  const multiCurrencySuffix = regionalPrices.length > 0 ? ` · ${regionalPrices.join(', ')}` : '';

  if (price.type === 'recurring' && price.recurring) {
    const { interval, intervalCount } = price.recurring;
    if (intervalCount === 1) {
      return `${amount}/${interval}${multiCurrencySuffix}`;
    }
    return `${amount} every ${intervalCount} ${interval}s${multiCurrencySuffix}`;
  }

  return `${amount} (one-time)${multiCurrencySuffix}`;
}

function formatCurrencyAmount(currency: string, unitAmount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(unitAmount / 100);
}

/**
 * Format coupon discount for display
 * Shows either percent off or amount off
 */
function formatCouponDiscount(coupon: StripeCoupon): string {
  if (coupon.percentOff) {
    return `${coupon.percentOff}% off`;
  }
  if (coupon.amountOff && coupon.currency) {
    const amount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: coupon.currency.toUpperCase(),
    }).format(coupon.amountOff / 100);
    return `${amount} off`;
  }
  return 'Discount';
}

/**
 * Format coupon duration for display
 */
function formatCouponDuration(coupon: StripeCoupon): string {
  switch (coupon.duration) {
    case 'forever':
      return 'Forever';
    case 'once':
      return 'First payment only';
    case 'repeating':
      if (coupon.durationInMonths) {
        return `${coupon.durationInMonths} month${coupon.durationInMonths > 1 ? 's' : ''}`;
      }
      return 'Limited time';
    default:
      return '';
  }
}

export default PaywallProductDialog;
